/**
 * Canonical ad id for URLs. Some Web Share targets incorrectly merge `text` + `url` into one string,
 * which lands in `?ad=` as "ad_xxx Title — R$…"; we strip to the id prefix.
 */
export function sanitizeAdIdForShare(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  const prefixed = s.match(/^ad_[A-Za-z0-9_-]+/);
  if (prefixed) return prefixed[0];
  return s.split(/\s/)[0] ?? "";
}

/**
 * Parse `ad` query value after cold load (tolerates corrupted share links).
 */
export function parseAdIdFromDeepLinkParam(raw: string | null | undefined): string {
	return sanitizeAdIdForShare(raw ?? "");
}

/**
 * Parses legacy path `/ad/<ad_id>` (e.g. Instagram QR) to the same id shape as `?ad=`.
 */
export function parseAdIdFromPathname(pathname: string | null | undefined): string {
	const p = (pathname ?? "").replace(/\/+$/, "") || "";
	const m = p.match(/^\/ad\/(ad_[A-Za-z0-9_-]+)$/i);
	return m ? sanitizeAdIdForShare(m[1]) : "";
}

/** Production web origin for share links (always akihub unless overridden for staging). */
const DEFAULT_PUBLIC_WEB_ORIGIN = "https://www.akihub.com.br";

function publicWebOriginForShare(): string {
  const raw = (
    import.meta.env.VITE_PUBLIC_WEB_ORIGIN ??
    import.meta.env.PRD_PUBLIC_WEB_ORIGIN ??
    ""
  )
    .toString()
    .trim()
    .replace(/\/$/, "");
  return raw || DEFAULT_PUBLIC_WEB_ORIGIN;
}

/**
 * Stable, shareable URL for an ad. Uses query param `ad=<ad_id>` so a cold load can open the detail page.
 * Origin is the public site (default https://www.akihub.com.br), not window.location, so links stay correct
 * when opened from admin/preview or an old host. Override with VITE_PUBLIC_WEB_ORIGIN or PRD_PUBLIC_WEB_ORIGIN.
 */
export function buildAdShareUrl(adId: string): string {
  const id = sanitizeAdIdForShare(adId);
  if (!id) return "";
  const url = new URL("/", publicWebOriginForShare());
  url.searchParams.set("ad", id);
  return url.toString();
}

/**
 * Removes `ad` from the visible URL after the SPA has consumed the deep link.
 * Preserves pathname, hash, and any other query params.
 */
export function removeAdDeepLinkFromAddressBar(): void {
	if (typeof window === "undefined") return;
	const url = new URL(window.location.href);
	const hadQueryAd = url.searchParams.has("ad");
	const path = (url.pathname || "").replace(/\/+$/, "") || "/";
	const hadPathAd = !!parseAdIdFromPathname(path);
	if (!hadQueryAd && !hadPathAd) return;
	if (hadQueryAd) url.searchParams.delete("ad");
	if (hadPathAd) {
		url.pathname = "/";
	}
	const q = url.searchParams.toString();
	const next = url.pathname + (q ? `?${q}` : "") + url.hash;
	window.history.replaceState(window.history.state, "", next);
}
